/** Profile fields and procedure content retained by the things-and-facts model. */
import type { WorkSkillSection, WorkSkillStep } from "@/api/work-skills";
import { translate } from "@/i18n";
import { libMemoryMessages } from "@/i18n/messages/lib-memory";

/** Profile fields in display order, with the catalogue key of their label. */
const PROFILE_FIELDS = [
  ["role", "profileRole"],
  ["focus", "profileFocus"],
  ["audience", "profileAudience"],
  ["working_style", "profileWorkingStyle"],
  ["business_url", "profileWebsite"],
] as const;
export type ProfileField = (typeof PROFILE_FIELDS)[number][0];

/** Filled profile fields as [field, label, value], in display order. */
export function profileEntries(
  payload: Readonly<Record<string, unknown>>,
): Array<[ProfileField, string, string]> {
  return PROFILE_FIELDS.flatMap(([field, labelKey]) => {
    const value = payload[field];
    return typeof value === "string" && value.trim()
      ? [
          [field, translate(libMemoryMessages, labelKey), value.trim()] as [
            ProfileField,
            string,
            string,
          ],
        ]
      : [];
  });
}

export function procedureTitle(
  payload: Readonly<Record<string, unknown>>,
): string {
  return typeof payload.title === "string" ? payload.title.trim() : "";
}

/** The steps a procedure would save, including changes to an adapted playbook. */
export function procedureSections(
  payload: Readonly<Record<string, unknown>>,
): WorkSkillSection[] {
  if (Array.isArray(payload.sections))
    return (payload.sections as WorkSkillSection[]).filter((section) =>
      Array.isArray(section.steps),
    );
  const overlay = Array.isArray(payload.overlay) ? payload.overlay : [];
  const steps = overlay.flatMap((operation): WorkSkillStep[] => {
    const step = operation as Record<string, unknown>;
    const text = typeof step.text === "string" ? step.text.trim() : "";
    const id =
      typeof step.step_id === "string" && step.step_id.trim()
        ? step.step_id.trim()
        : text;
    return text ? [{ step_id: id, text }] : [];
  });
  return steps.length > 0
    ? [{ title: translate(libMemoryMessages, "procedureYourChanges"), steps }]
    : [];
}

export function procedureChecks(
  payload: Readonly<Record<string, unknown>>,
): string[] {
  const checks = payload.quality_bar;
  return Array.isArray(checks)
    ? checks.filter(
        (check): check is string =>
          typeof check === "string" && check.trim() !== "",
      )
    : [];
}
